import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { OrchestrationService } from '../services';

// UUID generator with fallback for environments without crypto.randomUUID
const generateUUID = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
import type {
  AgentExecution,
  Citation,
  Memory,
  MemoryType,
  MissingCitationEvent,
  OrchestrationEvent,
  OrchestrationPlan,
  OrchestrationRequest,
  OrchestrationResult,
  OrchestrationState,
  ResearchMode,
  ThinkingStep,
  WorkspaceContext,
} from '../types';

/**
 * Hook for accessing the orchestration service
 */
export function useOrchestration() {
  const service = useService(OrchestrationService);
  return service;
}

/**
 * Hook for orchestration state
 */
export function useOrchestrationState(): OrchestrationState {
  const service = useService(OrchestrationService);
  const [state, setState] = useState<OrchestrationState>(service.state);

  useEffect(() => {
    const subscription = service.state$.subscribe(setState);
    return () => subscription.unsubscribe();
  }, [service]);

  return state;
}

/**
 * Hook for executing orchestration requests (non-streaming)
 */
export function useOrchestrationExecute() {
  const service = useService(OrchestrationService);
  const [result, setResult] = useState<OrchestrationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (request: OrchestrationRequest): Promise<OrchestrationResult> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await service.execute(request);
        setResult(result);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [service]
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    service.reset();
  }, [service]);

  return {
    execute,
    result,
    isLoading,
    error,
    reset,
  };
}

// ======== Progress Types ========

export interface OrchestrationProgress {
  stage:
    | 'initializing'
    | 'planning'
    | 'executing'
    | 'synthesizing'
    | 'complete';
  planId?: string;
  detectedMode?: ResearchMode;
  activeAgents: AgentExecution[];
  citations: Citation[];
  percentage?: number;
  message?: string;
}

/**
 * Hook for executing orchestration with SSE streaming
 *
 * Provides real-time progress updates during orchestration execution.
 */
export function useOrchestrationStreaming() {
  const service = useService(OrchestrationService);
  const [result, setResult] = useState<OrchestrationResult | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<OrchestrationProgress | null>(null);

  // Subscribe to service events for progress updates
  useEffect(() => {
    const subscription = service.events$.subscribe(event => {
      switch (event.type) {
        case 'plan-start':
          setProgress(prev => ({
            stage: 'planning',
            planId: event.planId,
            detectedMode: event.mode,
            activeAgents: prev?.activeAgents || [],
            citations: prev?.citations || [],
          }));
          break;

        case 'plan-ready':
          setProgress(prev => ({
            ...prev!,
            stage: 'executing',
            planId: event.planId,
          }));
          break;

        case 'agent-event': {
          const agentEvent = event.event;
          if (agentEvent.type === 'started') {
            setProgress(prev => ({
              ...prev!,
              activeAgents: [
                ...(prev?.activeAgents || []),
                {
                  id: generateUUID(),
                  sessionId: '',
                  agentType: agentEvent.agentType,
                  agentName: agentEvent.agentType,
                  task: agentEvent.task,
                  status: 'running' as const,
                  startedAt: new Date(),
                },
              ],
            }));
          } else if (agentEvent.type === 'progress') {
            setProgress(prev => ({
              ...prev!,
              message: agentEvent.message,
              percentage: agentEvent.percentage,
            }));
          } else if (agentEvent.type === 'complete') {
            setProgress(prev => ({
              ...prev!,
              activeAgents: (prev?.activeAgents || []).filter(
                a => a.agentType !== agentEvent.agentType
              ),
              citations: [
                ...(prev?.citations || []),
                ...(agentEvent.result.citations || []),
              ],
            }));
          }
          break;
        }

        case 'citation':
          setProgress(prev => ({
            ...prev!,
            citations: [...(prev?.citations || []), event.citation],
          }));
          break;

        case 'synthesis-start':
          setProgress(prev => ({
            ...prev!,
            stage: 'synthesizing',
          }));
          break;

        case 'synthesis-progress':
          setProgress(prev => ({
            ...prev!,
            message: event.message,
            percentage: event.percentage,
          }));
          break;

        case 'complete':
          setResult(event.result);
          setProgress(prev => ({
            ...prev!,
            stage: 'complete',
          }));
          break;

        case 'error':
          setError(event.error);
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, [service]);

  const execute = useCallback(
    async (request: OrchestrationRequest): Promise<OrchestrationResult> => {
      setIsStreaming(true);
      setError(null);
      setResult(null);
      setProgress({
        stage: 'initializing',
        activeAgents: [],
        citations: [],
      });

      try {
        const result = await service.executeWithStreaming(request);
        setResult(result);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        throw err;
      } finally {
        setIsStreaming(false);
      }
    },
    [service]
  );

  const cancel = useCallback(
    async (planId: string) => {
      await service.cancelPlan(planId);
      setIsStreaming(false);
      setProgress(null);
    },
    [service]
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setProgress(null);
    service.reset();
  }, [service]);

  return {
    execute,
    cancel,
    reset,
    result,
    isStreaming,
    error,
    progress,
  };
}

/**
 * Hook for real-time text streaming orchestration (Claude Code style)
 *
 * This hook provides character-by-character text streaming during synthesis,
 * giving users immediate feedback as AI generates responses.
 *
 * Uses 60fps throttled rendering for smooth UI updates.
 */
export function useStreamingText() {
  const service = useService(OrchestrationService);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OrchestrationResult | null>(null);
  const [progress, setProgress] = useState<OrchestrationProgress | null>(null);

  // Use requestAnimationFrame for 60fps throttled updates
  const pendingTextRef = { current: '' };
  const rafIdRef = { current: null as number | null };

  // Subscribe to service events for text-delta and progress updates
  useEffect(() => {
    const subscription = service.events$.subscribe(event => {
      switch (event.type) {
        case 'plan-start':
          setProgress({
            stage: 'planning',
            planId: event.planId,
            detectedMode: event.mode,
            activeAgents: [],
            citations: [],
          });
          break;

        case 'plan-ready':
          setProgress(prev => ({
            ...prev!,
            stage: 'executing',
            planId: event.planId,
          }));
          break;

        case 'agent-event': {
          const agentEvent = event.event;
          if (agentEvent.type === 'started') {
            setProgress(prev => ({
              ...prev!,
              activeAgents: [
                ...(prev?.activeAgents || []),
                {
                  id: generateUUID(),
                  sessionId: '',
                  agentType: agentEvent.agentType,
                  agentName: agentEvent.agentType,
                  task: agentEvent.task,
                  status: 'running' as const,
                  startedAt: new Date(),
                },
              ],
            }));
          } else if (agentEvent.type === 'complete') {
            setProgress(prev => ({
              ...prev!,
              activeAgents: (prev?.activeAgents || []).filter(
                a => a.agentType !== agentEvent.agentType
              ),
              citations: [
                ...(prev?.citations || []),
                ...(agentEvent.result.citations || []),
              ],
            }));
          }
          break;
        }

        case 'text-delta':
          // Accumulate text and throttle UI updates to 60fps
          pendingTextRef.current += event.text;

          if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(() => {
              setStreamingText(pendingTextRef.current);
              rafIdRef.current = null;
            });
          }
          break;

        case 'citation':
          setProgress(prev => ({
            ...prev!,
            citations: [...(prev?.citations || []), event.citation],
          }));
          break;

        case 'synthesis-start':
          setProgress(prev => ({
            ...prev!,
            stage: 'synthesizing',
          }));
          // Reset streaming text for synthesis
          pendingTextRef.current = '';
          setStreamingText('');
          break;

        case 'complete':
          // Final flush of any pending text
          if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
          }
          setStreamingText(pendingTextRef.current);
          setResult(event.result);
          setProgress(prev => ({
            ...prev!,
            stage: 'complete',
          }));
          break;

        case 'error':
          setError(event.error);
          break;
      }
    });

    return () => {
      subscription.unsubscribe();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [service]);

  const execute = useCallback(
    async (request: OrchestrationRequest): Promise<OrchestrationResult> => {
      setIsStreaming(true);
      setError(null);
      setResult(null);
      setStreamingText(null);
      pendingTextRef.current = '';
      setProgress({
        stage: 'initializing',
        activeAgents: [],
        citations: [],
      });

      try {
        // Endpoint selection:
        //   - Development (`BUILD_CONFIG.debug === true`): use the
        //     unauthenticated test endpoint at `/api/copilot/orchestration/test`.
        //   - Production: use the authenticated GraphQL mutation +
        //     subscription pipeline (`executeWithStreaming`).
        // The switch is driven by `BUILD_CONFIG.debug` so behaviour
        // matches the rest of the app without a separate env var.
        const useDev = BUILD_CONFIG.debug;
        const result = useDev
          ? await service.executeWithStreamingDev(request)
          : await service.executeWithStreaming(request);
        setResult(result);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        throw err;
      } finally {
        setIsStreaming(false);
      }
    },
    [service]
  );

  const cancel = useCallback(async (_planId: string) => {
    // DEV: cancel doesn't require GraphQL in dev mode
    setIsStreaming(false);
    setStreamingText(null);
    setProgress(null);
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setStreamingText(null);
    setProgress(null);
    pendingTextRef.current = '';
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    service.reset();
  }, [service]);

  return {
    execute,
    cancel,
    reset,
    streamingText, // Live text as it arrives
    result, // Final result after completion
    isStreaming,
    error,
    progress,
  };
}

/**
 * Hook for research mode selection with auto-detection
 */
export function useResearchMode() {
  const [mode, setMode] = useState<ResearchMode>('auto');
  const [detectedMode, setDetectedMode] = useState<ResearchMode | null>(null);
  const [confidence, setConfidence] = useState<number | null>(null);
  const service = useService(OrchestrationService);

  // Listen for mode-detected events from streaming
  useEffect(() => {
    const subscription = service.events$.subscribe(event => {
      if (event.type === 'plan-start' && event.mode !== 'auto') {
        setDetectedMode(event.mode);
      }
    });

    return () => subscription.unsubscribe();
  }, [service]);

  const selectMode = useCallback((newMode: ResearchMode) => {
    setMode(newMode);
    // Reset detected mode when user manually selects
    if (newMode !== 'auto') {
      setDetectedMode(null);
      setConfidence(null);
    }
  }, []);

  const effectiveMode = useMemo(() => {
    if (mode === 'auto' && detectedMode) {
      return detectedMode;
    }
    return mode;
  }, [mode, detectedMode]);

  return {
    mode,
    setMode: selectMode,
    detectedMode,
    confidence,
    effectiveMode,
  };
}

/**
 * Hook for subscribing to orchestration events
 */
export function useOrchestrationEvents(
  sessionId: string | undefined,
  onEvent?: (event: OrchestrationEvent) => void
) {
  const service = useService(OrchestrationService);
  const [events, setEvents] = useState<OrchestrationEvent[]>([]);

  useEffect(() => {
    if (!sessionId) return;

    const subscription = service.events$.subscribe(event => {
      setEvents(prev => [...prev, event]);
      onEvent?.(event);
    });

    return () => subscription.unsubscribe();
  }, [service, sessionId, onEvent]);

  return events;
}

/**
 * Hook for workspace context management
 */
export function useWorkspaceContext(workspaceId: string) {
  const service = useService(OrchestrationService);
  const [context, setContext] = useState<WorkspaceContext | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const ctx = await service.getWorkspaceContext(workspaceId);
      setContext(ctx);
    } finally {
      setIsLoading(false);
    }
  }, [service, workspaceId]);

  const update = useCallback(
    async (updates: Partial<WorkspaceContext>) => {
      const updatedContext = await service.updateWorkspaceContext(
        workspaceId,
        updates
      );
      setContext(updatedContext);
      return updatedContext;
    },
    [service, workspaceId]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    context,
    isLoading,
    refresh,
    update,
  };
}

/**
 * Hook for memory management
 */
export function useOrchestrationMemories(
  workspaceId: string,
  options?: {
    type?: MemoryType;
    limit?: number;
  }
) {
  const service = useService(OrchestrationService);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const mems = await service.searchMemories(workspaceId, options);
      setMemories(mems);
    } finally {
      setIsLoading(false);
    }
  }, [service, workspaceId, options]);

  const createMemory = useCallback(
    async (memory: {
      type: MemoryType;
      content: string;
      sessionId?: string;
      metadata?: Record<string, unknown>;
    }) => {
      const created = await service.createMemory({
        workspaceId,
        ...memory,
      });
      setMemories(prev => [...prev, created]);
      return created;
    },
    [service, workspaceId]
  );

  const deleteMemory = useCallback(
    async (memoryId: string) => {
      const deleted = await service.deleteMemory(workspaceId, memoryId);
      if (deleted) {
        setMemories(prev => prev.filter(m => m.id !== memoryId));
      }
      return deleted;
    },
    [service, workspaceId]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    memories,
    isLoading,
    refresh,
    createMemory,
    deleteMemory,
  };
}

/**
 * Hook for citations from orchestration results
 */
export function useOrchestrationCitations() {
  const state = useOrchestrationState();
  return state.citations;
}

/**
 * Hook for active agents during orchestration
 */
export function useActiveAgents() {
  const state = useOrchestrationState();
  return Array.from(state.activeAgents.values());
}

/**
 * Hook for orchestration history
 */
export function useOrchestrationHistory(
  workspaceId: string,
  options?: { limit?: number }
) {
  const service = useService(OrchestrationService);
  const [history, setHistory] = useState<OrchestrationPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const plans = await service.getHistory(workspaceId, {
        limit: options?.limit || 20,
      });
      setHistory(plans);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load history';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [service, workspaceId, options?.limit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    history,
    isLoading,
    error,
    refresh,
  };
}

/**
 * Hook for building thinking step UI from orchestration events.
 *
 * Processes step-start, step-progress, step-complete, and step-error events
 * to build a tree of ThinkingStep objects for the ThinkingContainer UI.
 */
export function useThinkingSteps() {
  const service = useService(OrchestrationService);
  const [steps, setSteps] = useState<ThinkingStep[]>([]);
  const [detectedMode, setDetectedMode] = useState<ResearchMode | undefined>(
    undefined
  );
  const stepCounterRef = { current: 0 };

  useEffect(() => {
    const subscription = service.events$.subscribe(event => {
      switch (event.type) {
        case 'plan-start':
          setDetectedMode(event.mode !== 'auto' ? event.mode : undefined);
          setSteps([]);
          stepCounterRef.current = 0;
          break;

        case 'step-start':
          setSteps(prev => {
            // Mark any currently in_progress step as done if it wasn't explicitly completed
            const updated = prev.map(s =>
              s.status === 'in_progress'
                ? {
                    ...s,
                    status: 'done' as const,
                    expanded: true,
                    collapsible: true,
                  }
                : s
            );
            return [
              ...updated,
              {
                id: event.stepId,
                title: event.title,
                status: 'in_progress' as const,
                subSteps: [],
                collapsible: false,
                expanded: true,
                agentType: event.agentType,
              },
            ];
          });
          break;

        case 'step-progress':
          setSteps(prev =>
            prev.map(step => {
              if (step.id !== event.stepId) return step;
              stepCounterRef.current += 1;
              // Mark previous sub-steps as done
              const updatedSubSteps = step.subSteps.map(ss =>
                ss.status === 'in_progress'
                  ? { ...ss, status: 'done' as const }
                  : ss
              );
              return {
                ...step,
                collapsible: true,
                subSteps: [
                  ...updatedSubSteps,
                  {
                    id: `${event.stepId}-sub-${stepCounterRef.current}`,
                    icon: event.subStep.icon,
                    text: event.subStep.text,
                    detail: event.subStep.detail,
                    status: 'in_progress' as const,
                  },
                ],
              };
            })
          );
          break;

        case 'step-complete':
          setSteps(prev =>
            prev.map(step => {
              if (step.id !== event.stepId) return step;
              // Mark all sub-steps as done
              const completedSubSteps = step.subSteps.map(ss => ({
                ...ss,
                status: 'done' as const,
              }));
              return {
                ...step,
                status: 'done' as const,
                subSteps: completedSubSteps,
                // Card stays expanded by default so users can see the sources
                // — matches the Claude video. Clicking the chevron collapses.
                collapsible: true,
                expanded: true,
                summary: event.summary,
                citationsFound: event.citationsFound,
                query: event.query,
                sources: event.sources,
              };
            })
          );
          break;

        case 'step-error':
          setSteps(prev =>
            prev.map(step => {
              if (step.id !== event.stepId) return step;
              return {
                ...step,
                status: 'error' as const,
                error: event.error,
                expanded: true, // Keep expanded to show error
              };
            })
          );
          break;

        case 'synthesis-start':
          // Add a synthesis step
          setSteps(prev => {
            const updated = prev.map(s =>
              s.status === 'in_progress'
                ? {
                    ...s,
                    status: 'done' as const,
                    expanded: false,
                    collapsible: true,
                  }
                : s
            );
            return [
              ...updated,
              {
                id: 'synthesis',
                title: 'Synthesizing findings',
                status: 'in_progress' as const,
                subSteps: [],
                collapsible: false,
                expanded: true,
              },
            ];
          });
          break;

        case 'synthesis-progress':
          setSteps(prev =>
            prev.map(step => {
              if (step.id !== 'synthesis') return step;
              stepCounterRef.current += 1;
              return {
                ...step,
                collapsible: true,
                subSteps: [
                  ...step.subSteps,
                  {
                    id: `synthesis-sub-${stepCounterRef.current}`,
                    icon: 'sparkles' as const,
                    text: event.message,
                    status: 'in_progress' as const,
                  },
                ],
              };
            })
          );
          break;

        case 'complete':
          // Mark synthesis step as done
          setSteps(prev =>
            prev.map(step =>
              step.id === 'synthesis' && step.status === 'in_progress'
                ? {
                    ...step,
                    status: 'done' as const,
                    expanded: false,
                    collapsible: true,
                    subSteps: step.subSteps.map(ss => ({
                      ...ss,
                      status: 'done' as const,
                    })),
                  }
                : step
            )
          );
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, [service]);

  const toggleExpand = useCallback((stepId: string) => {
    setSteps(prev =>
      prev.map(step =>
        step.id === stepId && step.collapsible
          ? { ...step, expanded: !step.expanded }
          : step
      )
    );
  }, []);

  const reset = useCallback(() => {
    setSteps([]);
    setDetectedMode(undefined);
    stepCounterRef.current = 0;
  }, []);

  return {
    steps,
    detectedMode,
    toggleExpand,
    reset,
  };
}

export type CitationExportFormat =
  | 'bibtex'
  | 'apa'
  | 'mla'
  | 'chicago'
  | 'json';

/**
 * Hook for exporting aggregated session citations. Returns an `exportTo`
 * function plus loading/error state for the most recent invocation.
 */
export function useExportCitations() {
  const service = useService(OrchestrationService);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportTo = useCallback(
    async (
      workspaceId: string,
      sessionId: string,
      format: CitationExportFormat
    ): Promise<string> => {
      setIsExporting(true);
      setError(null);
      try {
        return await service.exportCitations(workspaceId, sessionId, format);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed';
        setError(message);
        throw err;
      } finally {
        setIsExporting(false);
      }
    },
    [service]
  );

  return { exportTo, isExporting, error };
}

/**
 * Build a stable key for deduping citations across executions.
 */
function citationKey(c: Citation): string {
  return `${c.type ?? 'unknown'}:${c.identifier ?? c.id}`;
}

/**
 * Merge an incoming citation into the list. If a citation with the same key
 * already exists, the incoming one shallow-overrides (preferring populated
 * fields). This lets the backend emit an initial `citation` event from an
 * agent, then a second from the verifier that refines `verified`, `title`,
 * etc., without losing the update to dedupe.
 */
function mergeCitation(prev: Citation[], incoming: Citation): Citation[] {
  const key = citationKey(incoming);
  const idx = prev.findIndex(c => citationKey(c) === key);
  if (idx === -1) return [...prev, incoming];
  const existing = prev[idx];
  const overrides: Partial<Citation> = {};
  for (const [k, v] of Object.entries(incoming) as [
    keyof Citation,
    Citation[keyof Citation],
  ][]) {
    if (v !== undefined && v !== null && v !== '') {
      (overrides as Record<string, unknown>)[k] = v;
    }
  }
  const merged = { ...existing, ...overrides };
  const next = prev.slice();
  next[idx] = merged;
  return next;
}

/**
 * Subscribe to streaming citation events and expose a deduped list plus any
 * "[NEEDS-CITATION:...]" markers the synthesis emitted. Reset whenever a new
 * plan starts.
 */
export function useCitationStream(): {
  citations: Citation[];
  missingCitations: MissingCitationEvent[];
  reset: () => void;
} {
  const service = useService(OrchestrationService);
  const [citations, setCitations] = useState<Citation[]>([]);
  const [missingCitations, setMissingCitations] = useState<
    MissingCitationEvent[]
  >([]);

  useEffect(() => {
    const subscription = service.events$.subscribe(event => {
      switch (event.type) {
        case 'plan-start':
          setCitations([]);
          setMissingCitations([]);
          break;
        case 'citation':
          setCitations(prev => mergeCitation(prev, event.citation));
          break;
        case 'agent-event':
          if (event.event.type === 'complete') {
            const incoming = event.event.result?.citations ?? [];
            if (!incoming.length) break;
            setCitations(prev => {
              let next = prev;
              for (const c of incoming) next = mergeCitation(next, c);
              return next;
            });
          }
          break;
        case 'missing-citation':
          setMissingCitations(prev => [...prev, event]);
          break;
      }
    });
    return () => subscription.unsubscribe();
  }, [service]);

  const reset = useCallback(() => {
    setCitations([]);
    setMissingCitations([]);
  }, []);

  return { citations, missingCitations, reset };
}

/**
 * Hook for available agents
 */
export function useAvailableAgents(workspaceId: string) {
  const service = useService(OrchestrationService);
  const [agents, setAgents] = useState<
    Array<{
      name: string;
      agentType: string;
      description: string;
      tools?: string[];
      model?: string;
      source: 'built-in' | 'workspace';
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    service
      .listAgents(workspaceId)
      .then(setAgents)
      .finally(() => setIsLoading(false));
  }, [service, workspaceId]);

  return { agents, isLoading };
}
